import type { DragEndEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "@lingui/macro";
import {
  CaretDownIcon,
  CaretUpIcon,
  DotsSixVerticalIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { Button, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

/**
 * ExperienceForm — the repeatable "multi-entry with rich text" pattern
 * (Apply Edge builder v2). Serves Experience, Education, Volunteering, Projects,
 * Publications — pass `labels` to relabel.
 *
 * Fully controlled. Each entry's flat fields come through `onChangeEntry`; the
 * rich text description is a slot the parent renders via `renderDescription`.
 * Drag-to-reorder is presentational only — spread `getDragHandleProps(id)` onto
 * the handle; the parent owns the DnD logic.
 */
export type ExperienceEntry = {
  id: string;
  title: string;
  subtitle: string;
  location?: string;
  /** ISO date strings (yyyy-mm-dd). `endDate` empty when `current`. */
  startDate: string;
  endDate: string;
  current?: boolean;
  url?: { label: string; href: string };
};

export type ExperienceFormProps = {
  entries: ExperienceEntry[];
  onChangeEntry: (id: string, patch: Partial<ExperienceEntry>) => void;
  onAddEntry?: () => void;
  onRemoveEntry?: (id: string) => void;
  /** Rich text editor for an entry's description. Wired by the parent. */
  renderDescription?: (entryId: string) => ReactNode;
  openEntryId?: string | null;
  onToggleEntry?: (id: string) => void;
  onReorderEntries?: (newEntries: ExperienceEntry[]) => void;
  labels?: { title?: string; subtitle?: string; addEntry?: string };
};

const inputCls =
  "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"; // eslint-disable-line lingui/no-unlocalized-strings

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <span className="mb-1.5 block font-mono text-[10px] text-muted-foreground">{children}</span>
);

type SortableEntryProps = {
  entry: ExperienceEntry;
  open: boolean;
  titleLabel: string;
  subtitleLabel: string;
  onChangeEntry: (id: string, patch: Partial<ExperienceEntry>) => void;
  onRemoveEntry?: (id: string) => void;
  onToggleEntry?: (id: string) => void;
  renderDescription?: (entryId: string) => ReactNode;
};

const SortableEntry = ({
  entry,
  open,
  titleLabel,
  subtitleLabel,
  onChangeEntry,
  onRemoveEntry,
  onToggleEntry,
  renderDescription,
}: SortableEntryProps) => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: entry.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
    transition,
  };

  const heading = entry.title || titleLabel;
  const sub = entry.subtitle;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "overflow-hidden rounded-xl border bg-background",
        open ? "border-border" : "border-border",
      )}
    >
      <div className="flex items-center gap-2 p-2.5">
        <button
          type="button"
          aria-label={t`Drag to reorder`}
          className="flex h-7 w-5 cursor-grab items-center justify-center text-zinc-400 hover:text-foreground active:cursor-grabbing"
          {...listeners}
          {...attributes}
        >
          <DotsSixVerticalIcon size={16} />
        </button>

        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          aria-expanded={open}
          onClick={() => onToggleEntry?.(entry.id)}
        >
          <span className="block truncate text-sm font-medium text-foreground">{heading}</span>
          {sub ? <span className="block truncate text-xs text-muted-foreground">{sub}</span> : null}
        </button>

        {onRemoveEntry ? (
          <Tooltip content={t`Delete entry`}>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-muted-foreground hover:text-rose-500"
              aria-label={t`Delete entry`}
              onClick={() => {
                onRemoveEntry(entry.id);
              }}
            >
              <TrashIcon size={15} />
            </Button>
          </Tooltip>
        ) : null}
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          aria-label={open ? t`Collapse entry` : t`Expand entry`}
          onClick={() => onToggleEntry?.(entry.id)}
        >
          {open ? <CaretUpIcon size={15} /> : <CaretDownIcon size={15} />}
        </Button>
      </div>

      {open ? (
        <div className="border-t border-border p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label>
              <FieldLabel>{titleLabel}</FieldLabel>
              <input
                className={inputCls}
                value={entry.title}
                aria-label={titleLabel}
                onChange={(e) => {
                  onChangeEntry(entry.id, { title: e.target.value });
                }}
              />
            </label>
            <label>
              <FieldLabel>{subtitleLabel}</FieldLabel>
              <input
                className={inputCls}
                value={entry.subtitle}
                aria-label={subtitleLabel}
                onChange={(e) => {
                  onChangeEntry(entry.id, { subtitle: e.target.value });
                }}
              />
            </label>
            <label className="sm:col-span-2">
              <FieldLabel>{t`Location`}</FieldLabel>
              <input
                className={inputCls}
                value={entry.location ?? ""}
                aria-label={t`Location`}
                onChange={(e) => {
                  onChangeEntry(entry.id, { location: e.target.value });
                }}
              />
            </label>
            <label className="sm:col-span-2">
              <FieldLabel>{t`URL`}</FieldLabel>
              <input
                className={inputCls}
                type="url"
                value={entry.url?.href ?? ""}
                placeholder={t`https://example.com`}
                aria-label={t`URL`}
                onChange={(e) => {
                  onChangeEntry(entry.id, {
                    url: {
                      href: e.target.value,
                      label: entry.url?.label ?? "",
                    },
                  });
                }}
              />
            </label>
            <label className="sm:col-span-2">
              <FieldLabel>{t`URL label`}</FieldLabel>
              <input
                className={inputCls}
                value={entry.url?.label ?? ""}
                placeholder={t`Optional display text for the link`}
                aria-label={t`URL label`}
                onChange={(e) => {
                  onChangeEntry(entry.id, {
                    url: {
                      href: entry.url?.href ?? "",
                      label: e.target.value,
                    },
                  });
                }}
              />
            </label>
            <label>
              <FieldLabel>{t`Start date`}</FieldLabel>
              <input
                className={inputCls}
                type="date"
                value={entry.startDate}
                aria-label={t`Start date`}
                onChange={(e) => {
                  onChangeEntry(entry.id, { startDate: e.target.value });
                }}
              />
            </label>
            <label>
              <FieldLabel>{t`End date`}</FieldLabel>
              <input
                className={inputCls}
                type="date"
                value={entry.endDate}
                disabled={entry.current}
                aria-label={t`End date`}
                onChange={(e) => {
                  onChangeEntry(entry.id, { endDate: e.target.value });
                }}
              />
            </label>
          </div>

          <label className="mt-3 flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-primary"
              checked={!!entry.current}
              onChange={(e) => {
                onChangeEntry(entry.id, {
                  current: e.target.checked,
                  endDate: e.target.checked ? "" : entry.endDate,
                });
              }}
            />
            {t`I currently work here`}
          </label>

          {renderDescription ? (
            <div className="mt-3">
              <FieldLabel>{t`Description`}</FieldLabel>
              {renderDescription(entry.id)}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const ExperienceForm = ({
  entries,
  onChangeEntry,
  onAddEntry,
  onRemoveEntry,
  renderDescription,
  openEntryId,
  onToggleEntry,
  onReorderEntries,
  labels,
}: ExperienceFormProps) => {
  const titleLabel = labels?.title ?? t`Role`;
  const subtitleLabel = labels?.subtitle ?? t`Organization`;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = entries.findIndex((e) => e.id === active.id);
    const newIndex = entries.findIndex((e) => e.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedEntries = arrayMove(entries, oldIndex, newIndex);
    onReorderEntries?.(reorderedEntries);
  };

  return (
    <div className="flex flex-col gap-2.5">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={entries.map((e) => e.id)} strategy={verticalListSortingStrategy}>
          {entries.map((entry) => {
            const open = openEntryId === entry.id;
            return (
              <SortableEntry
                key={entry.id}
                entry={entry}
                open={open}
                titleLabel={titleLabel}
                subtitleLabel={subtitleLabel}
                renderDescription={renderDescription}
                onChangeEntry={onChangeEntry}
                onRemoveEntry={onRemoveEntry}
                onToggleEntry={onToggleEntry}
              />
            );
          })}
        </SortableContext>
      </DndContext>

      <Button
        variant="ghost"
        className="h-auto w-full justify-center gap-2 rounded-xl border border-dashed border-border py-3 text-sm font-medium text-muted-foreground hover:border-primary hover:bg-primary/5 hover:text-primary"
        aria-label={labels?.addEntry ?? t`Add entry`}
        onClick={onAddEntry}
      >
        <PlusIcon size={16} />
        {labels?.addEntry ?? t`Add entry`}
      </Button>
    </div>
  );
};
